/**
 * Sub-tab "Acumulado" — compara enero→mesCorte año1 vs el mismo periodo año2.
 * Layout: full-width desktop (igual que Tabla Resumen / Resultados Mes).
 */
import React, { useEffect, useState } from 'react';
import { Select } from '../../../../../shared/components';
import { ComparativosMultiSelect } from './ComparativosMultiSelect';
import { getComparativosRepository } from '../../../../../shared/services/results/comparativosRepository';
import { getResultsRepository } from '../../../../../shared/services/results/repositoryFactory';
import { AccumulatedComparisonSummaryDto } from '../../../../../shared/services/results/types.comparativos';
import { ServiceType } from '../../../../../shared/services/results/types';
import { SummaryCards } from './SummaryCards';
import { ComparisonTable } from './ComparisonTable';
import { yearOptions, monthOptions, MONTH_LABELS } from './comparativosUtils';
import { exportAccumulatedExcel } from '../../../utils/exportComparativos';

const repo = getComparativosRepository();
const baseRepo = getResultsRepository();

export function AccumulatedTab() {
  const now = new Date();
  const currentMonthKey = [
    'enero','febrero','marzo','abril','mayo','junio',
    'julio','agosto','septiembre','octubre','noviembre','diciembre',
  ][now.getMonth()];

  const [mesCorte, setMesCorte] = useState(currentMonthKey);
  const [anio1, setAnio1] = useState(now.getFullYear() - 1);
  const [anio2, setAnio2] = useState(now.getFullYear());
  const [tiposServicio, setTiposServicio] = useState<string[]>([]);
  const [clientes, setClientes] = useState<string[]>([]);
  const [serviceTypes, setServiceTypes] = useState<ServiceType[]>([]);
  const [allClients, setAllClients] = useState<string[]>([]);
  const [data, setData] = useState<AccumulatedComparisonSummaryDto | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    Promise.all([baseRepo.listServiceTypes(), baseRepo.listDistinctClients()]).then(([st, cl]: [ServiceType[], string[]]) => {
      setServiceTypes(st);
      setAllClients(cl);
    });
  }, []);

  useEffect(() => {
    setIsLoading(true);
    repo
      .getAccumulatedComparison({ mesCorte, anio1, anio2, tiposServicio: tiposServicio.length ? tiposServicio : undefined, clientes: clientes.length ? clientes : undefined })
      .then((d: AccumulatedComparisonSummaryDto) => { setData(d); setIsLoading(false); })
      .catch((err) => { console.error('[AccumulatedTab]', err); setIsLoading(false); });
  }, [mesCorte, anio1, anio2, tiposServicio, clientes]);

  const yOpts = yearOptions(now.getFullYear());
  const rows = (data?.rows ?? []).map((r: AccumulatedComparisonSummaryDto['rows'][number]) => ({ ...r, valor1: r.totalAnio1, valor2: r.totalAnio2 }));
  const mesLabel = MONTH_LABELS[mesCorte] ?? mesCorte;
  const periodo1Label = `Ene–${mesLabel} ${anio1}`;
  const periodo2Label = `Ene–${mesLabel} ${anio2}`;

  return (
    <div className="relative left-1/2 -ml-[50vw] w-screen px-4 sm:px-6 lg:px-8">
      <div className="space-y-4">
        {/* Filtros */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5 lg:max-w-4xl">
          <Select label="Mes de corte" options={monthOptions} value={mesCorte} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setMesCorte(e.target.value)} />
          <Select label="Año 1 (base)" options={yOpts} value={String(anio1)} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setAnio1(Number(e.target.value))} />
          <Select label="Año 2 (comparar)" options={yOpts} value={String(anio2)} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setAnio2(Number(e.target.value))} />
          <ComparativosMultiSelect label="Tipo Servicio" options={serviceTypes.map((s) => s.nombre)} selected={tiposServicio} onApply={setTiposServicio} />
          <ComparativosMultiSelect label="Cliente" options={allClients} selected={clientes} onApply={setClientes} />
        </div>

        <p className="text-xs text-ink-400">
          Comparando acumulado <span className="font-semibold text-ink-700">enero → {mesLabel}</span> entre {anio1} y {anio2}.
        </p>

        {/* Botón Excel */}
        {data && (
          <div className="flex justify-end">
            <button
              onClick={() => exportAccumulatedExcel(data, periodo1Label, periodo2Label)}
              className="flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700 active:scale-95"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
              Descargar Excel
            </button>
          </div>
        )}

        {isLoading && <p className="py-8 text-center text-sm text-ink-400">Cargando comparativo acumulado...</p>}

        {data && !isLoading && (
          <>
            <SummaryCards
              periodo1Label={periodo1Label}
              periodo2Label={periodo2Label}
              ingreso1={data.ingresoAnio1} ingreso2={data.ingresoAnio2}
              gastos1={data.gastosAnio1} gastos2={data.gastosAnio2}
              utilidad1={data.utilidadAnio1} utilidad2={data.utilidadAnio2}
              varIngresoAbs={data.varIngresoAbs} varIngresoPct={data.varIngresoPct}
              varGastosAbs={data.varGastosAbs} varGastosPct={data.varGastosPct}
              varUtilidadAbs={data.varUtilidadAbs} varUtilidadPct={data.varUtilidadPct}
            />
            <ComparisonTable rows={rows} label1={periodo1Label} label2={periodo2Label} />
          </>
        )}
      </div>
    </div>
  );
}
